/**
 * Purchase wizard submission controller (WI-TV-W6-004).
 *
 * Purchase submits through the exact same lead submitter the refinance journey
 * uses (`createLeadSubmitter` in `refinance-submission.ts`); the only
 * purchase-specific seam is the request builder below. Everything the submitter
 * guarantees is therefore identical for both journeys: exactly one LeadIntake
 * call per deliberate attempt, in-flight dedupe, terminal accepted success recorded
 * before any observer runs, no automatic replay, correlation continuity across
 * the request header / telemetry / outward message, contained best-effort
 * telemetry and embedder notification, and sanitized exact-origin COMPLETE /
 * ERROR messages carrying no borrower PII, secrets, or raw payloads.
 *
 * The purchase builder projects the cumulative wizard answers through the
 * existing purchase bridge (`toPurchaseFormState` -> `buildPayload` ->
 * `buildLmdUpsertRequest`) and validates them with the authoritative
 * `validatePurchase` validator. The down-payment step still derives the existing
 * required `loanAmount` (`estimatedValue - downPayment`), and the original down
 * payment is carried with it for LeadIntake.
 */
import { buildLmdUpsertRequest } from "../form/lmd-mapper";
import { buildPayload } from "../form/payload-builder";
import { validatePurchase } from "../form/validation";
import {
  createLeadSubmitter,
  type LeadRequestResult,
  type RefinanceSubmissionDeps,
  type RefinanceSubmitter,
} from "./refinance-submission";
import { toPurchaseFormState } from "./purchase-values";

/**
 * Validates the cumulative purchase answers with the authoritative purchase
 * validator and returns the LMD request when they are submittable.
 */
function buildPurchaseRequest(values: Readonly<Record<string, string>>): LeadRequestResult {
  const built = buildPayload(toPurchaseFormState(values));
  if (!built.ok) return { ok: false, errors: [built.error] };
  if (built.purpose !== "purchase") {
    return { ok: false, errors: ["The purchase wizard can only submit a purchase referral."] };
  }

  const errors = validatePurchase(built.request);
  if (errors.length > 0) return { ok: false, errors };

  const result: LeadRequestResult = {
    ok: true,
    request: buildLmdUpsertRequest(built),
  };
  if (built.request.partnerReferenceId) result.partnerReferenceId = built.request.partnerReferenceId;
  return result;
}

/** Creates the purchase submitter used by the purchase wizard/journey. */
export function createPurchaseSubmitter(deps: RefinanceSubmissionDeps): RefinanceSubmitter {
  return createLeadSubmitter(deps, buildPurchaseRequest);
}
